import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Alert, Card, Col, Collapse, Row } from '@themesberg/react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartBar,
  faCheckCircle,
  faChevronDown,
  faClock,
  faMoneyBillWave,
  faPercentage,
  faReceipt,
  faSync,
  faTrophy,
  faWallet,
} from '@fortawesome/free-solid-svg-icons'
import { useCookies } from 'react-cookie'

import { getRevenueStatistics } from '../../../../controller/api/ticket'
import { Context } from '../Ticket'

const EMPTY_STATISTICS = {
  totalTicketAmount: '0',
  processingTicketAmount: '0',
  waitingRewardAmount: '0',
  completedTicketAmount: '0',
  totalPurchaseFee: '0',
  rewardGrossAmount: '0',
  rewardFee: '0',
  rewardNetAmount: '0',
}

const formatVnd = (value) => {
  const normalizedValue = String(value || '0')

  if (/^-?\d+$/.test(normalizedValue)) {
    const sign = normalizedValue.startsWith('-') ? '-' : ''
    const digits = normalizedValue.replace('-', '')
    return `${sign}${digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} ₫`
  }

  return `${new Intl.NumberFormat('vi-VN').format(Number(value) || 0)} ₫`
}

const statisticItems = [
  { key: 'totalTicketAmount', label: 'Tổng tiền vé', icon: faMoneyBillWave, color: '#2563eb' },
  { key: 'processingTicketAmount', label: 'Đang xử lý / đã in', icon: faReceipt, color: '#0ea5e9' },
  { key: 'waitingRewardAmount', label: 'Tổng tiền vé chờ tính thưởng', icon: faClock, color: '#8b5cf6' },
  { key: 'completedTicketAmount', label: 'Tổng tiền vé đã hoàn thành', icon: faCheckCircle, color: '#10b981' },
  { key: 'totalPurchaseFee', label: 'Tổng phí mua vé', icon: faPercentage, color: '#f59e0b' },
  { key: 'rewardGrossAmount', label: 'Tổng tiền trúng thưởng', icon: faTrophy, color: '#dc2626' },
  { key: 'rewardFee', label: 'Phí trả thưởng', icon: faChartBar, color: '#ea580c' },
  { key: 'rewardNetAmount', label: 'Thực nhận thưởng', icon: faWallet, color: '#059669' },
]

const RevenueStatistics = () => {
  const { state } = useContext(Context)
  const [cookies] = useCookies(['lottmekong'])
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [statistics, setStatistics] = useState(EMPTY_STATISTICS)
  const latestRequestRef = useRef(0)

  const loadStatistics = useCallback(async () => {
    const requestId = ++latestRequestRef.current
    setLoading(true)
    setError('')

    try {
      const response = await getRevenueStatistics(cookies.lottmekong, {
        ticketId: state.ticketId,
        cartId: state.cartId,
        gamePeriodId: state.gamePeriodId,
        gameId: state.gameId,
        gameScope: state.gameScope,
        from: state.from,
        to: state.to,
        phone: state.phone,
        customerName: state.customerName,
      })

      if (requestId !== latestRequestRef.current) return

      if (response) {
        setStatistics({ ...EMPTY_STATISTICS, ...response })
      } else {
        setError('Không tải được thống kê doanh thu.')
      }
    } catch (loadError) {
      console.error('Error fetching ticket revenue statistics:', loadError)
      if (requestId === latestRequestRef.current) {
        setError('Không tải được thống kê doanh thu.')
      }
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false)
      }
    }
  }, [cookies.lottmekong, state.ticketId, state.cartId, state.gamePeriodId, state.gameId, state.gameScope, state.from, state.to, state.phone, state.customerName])

  useEffect(() => {
    if (expanded && state.gameScope) {
      loadStatistics()
    }
  }, [expanded, loadStatistics, state.revenueRefreshKey, state.gameScope])

  return (
    <Card border="light" className="shadow-sm">
      <Card.Header className="bg-white p-0">
        <button
          type="button"
          className="w-100 border-0 bg-transparent px-3 py-2 text-start"
          aria-expanded={expanded}
          aria-controls="ticket-revenue-statistics"
          onClick={() => setExpanded(current => !current)}
        >
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div>
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faMoneyBillWave} className="me-2 text-success" />
                Thống kê doanh thu
              </h6>
            </div>
            <span className="text-primary text-nowrap">
              {expanded ? 'Thu gọn' : 'Mở rộng'}
              <FontAwesomeIcon
                icon={faChevronDown}
                className="ms-2"
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform .2s ease',
                }}
              />
            </span>
          </div>
        </button>
      </Card.Header>

      <Collapse in={expanded}>
        <div id="ticket-revenue-statistics">
          <Card.Body className="p-3 p-md-4">
            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

            {loading ? (
              <div className="text-center text-muted py-4">
                <FontAwesomeIcon icon={faSync} spin className="me-2" />
                Đang tổng hợp doanh thu...
              </div>
            ) : (
              <Row className="g-3">
                {statisticItems.map(item => (
                  <Col key={item.key} sm={6} xl={3}>
                    <div
                      className="h-100 rounded p-3"
                      style={{
                        background: '#f8fafc',
                        boxShadow: `inset 3px 0 0 ${item.color}, inset 0 0 0 1px #e5e7eb`,
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <small className="text-muted fw-bold">{item.label}</small>
                        <FontAwesomeIcon icon={item.icon} style={{ color: item.color }} />
                      </div>
                      <strong className="d-block" style={{ color: item.color, fontSize: 20 }}>
                        {formatVnd(statistics[item.key])}
                      </strong>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
            {!loading && !error && (
              <small className="d-block text-muted mt-3">
                Phí mua vé được cộng một lần theo giao dịch; phí trả thưởng là khoản khấu trừ từ tiền trúng thưởng.
              </small>
            )}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  )
}

export default React.memo(RevenueStatistics)
